import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, FileText, Loader2, X, CheckCircle, AlertCircle, ChevronDown } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { primaryText, semiBoldText, baseTextSize, smallTextSize } from '../../../styling/stylingHelper';
import { getAccessToken } from '../../../services/authentication/supabaseService';
import { Platform } from '../../../types/Platforms';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { refreshDemoHistory } from '../../../services/automation/refreshDemoHistory';

// Demo data type — drives both revenue source type and (for new sections) section type.
// TAKE_AWAY uses revenue source TAKE_AWAY but a FOOD section since there is no take-away section type.
enum SectionType {
  FOOD = 'FOOD',
  DRINKS = 'DRINKS',
  TAKE_AWAY = 'TAKE_AWAY',
}

interface DemoAutomationModalProps {
  restaurantId: string;
  onClose: () => void;
  onDone: () => void;
}

const DemoAutomationModal: React.FC<DemoAutomationModalProps> = ({ restaurantId, onClose, onDone }) => {
  const { t } = useTranslation();
  const { restaurantSections } = useRestaurantStore();
  const sections = restaurantSections[restaurantId] || [];

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [sectionType, setSectionType] = useState<SectionType>(SectionType.FOOD);
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>([]);
  const [catchAllPlatform, setCatchAllPlatform] = useState<Platform | ''>('');
  const [isPlatformDropdownOpen, setIsPlatformDropdownOpen] = useState(false);
  const [platformSearch, setPlatformSearch] = useState('');
  const [selectedSection, setSelectedSection] = useState<string>(''); // section ID or 'create_new'
  const [newSectionName, setNewSectionName] = useState<string>('');
  const [generateRevenueHistory, setGenerateRevenueHistory] = useState(true);
  const [generateOrderHistory, setGenerateOrderHistory] = useState(true);
  const [generateInventory, setGenerateInventory] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const platformDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (platformDropdownRef.current && !platformDropdownRef.current.contains(event.target as Node)) {
        setIsPlatformDropdownOpen(false);
      }
    };

    if (isPlatformDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPlatformDropdownOpen]);

  const formatPlatformName = (platform: Platform) => {
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  // Filter out namespace functions, only get enum values
  const allPlatforms = Object.values(Platform).filter(
    (p): p is Platform => typeof p === 'string'
  );
  const filteredPlatforms = allPlatforms.filter(platform =>
    formatPlatformName(platform).toLowerCase().includes(platformSearch.trim().toLowerCase())
  );

  const handlePlatformToggle = (platform: Platform) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const handleSelectAllPlatforms = () => {
    setSelectedPlatforms(allPlatforms);
  };

  const handleClearAllPlatforms = () => {
    setSelectedPlatforms([]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setResult(null);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setError(t('profile.demoAutomation.errors.selectFile'));
      return;
    }

    if (selectedPlatforms.length === 0) {
      setError(t('profile.demoAutomation.errors.selectSupplier'));
      return;
    }

    if (!catchAllPlatform) {
      setError(t('profile.demoAutomation.errors.selectFallbackSupplier'));
      return;
    }

    if (!selectedSection) {
      setError(t('profile.demoAutomation.errors.selectSection'));
      return;
    }

    if (selectedSection === 'create_new' && !newSectionName.trim()) {
      setError(t('profile.demoAutomation.errors.sectionName'));
      return;
    }

    // At least one generation option must be enabled
    if (!generateRevenueHistory && !generateOrderHistory) {
      setError(t('profile.demoAutomation.errors.selectGeneration'));
      return;
    }

    setIsProcessing(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('restaurantId', restaurantId);
      formData.append('sectionType', sectionType);
      formData.append('platforms', JSON.stringify(selectedPlatforms));
      formData.append('catchAllPlatform', catchAllPlatform);

      // Add section selection
      if (selectedSection === 'create_new') {
        formData.append('newSectionName', newSectionName.trim());
      } else {
        formData.append('restaurantSectionId', selectedSection);
      }

      formData.append('generateRevenueHistory', generateRevenueHistory.toString());
      formData.append('generateOrderHistory', generateOrderHistory.toString());
      formData.append('generateInventory', generateInventory.toString());
      formData.append('file', selectedFile);

      const accessToken = await getAccessToken();

      const response = await fetch('/api/automation/demo-automation', {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.details || data.error || t('profile.demoAutomation.errors.requestFailed'));
        return;
      }

      setResult(data);
      try {
        await refreshDemoHistory(restaurantId);
      } catch (refreshError) {
        console.error('Failed to refresh saved demo data:', refreshError);
        setError(t('profile.demoAutomation.refreshFailed'));
      }
    } catch (err: any) {
      console.error('Demo upload request failed:', err);
      setError(t('profile.demoAutomation.errors.requestFailed'));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black dark:bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-background dark:bg-background-dark rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className={cn(primaryText, semiBoldText, baseTextSize)}>
            Demo Automation
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <p className={cn(primaryText, smallTextSize, "opacity-70")}>
            Upload a menu template file (.docx) to generate demo revenue and order history data.
          </p>

          {/* File Upload */}
          <div>
            <label className={cn(primaryText, semiBoldText, smallTextSize, "block mb-2")}>
              Upload Template File
            </label>
            <div className="flex items-center gap-4">
              <label className="flex-1 flex items-center justify-center px-4 py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md cursor-pointer hover:border-accent dark:hover:border-accent-dark transition-colors">
                <div className="text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-2" />
                  <span className={cn(primaryText, smallTextSize)}>
                    {selectedFile ? selectedFile.name : 'Click to upload or drag and drop'}
                  </span>
                  <p className={cn(primaryText, "text-xs opacity-60 mt-1")}>
                    Word document (.docx) with menu items and ingredients
                  </p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleFileChange}
                />
              </label>
            </div>
            {selectedFile && (
              <div className="mt-2 flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                <FileText className="h-4 w-4" />
                <span>{selectedFile.name}</span>
              </div>
            )}
          </div>

          {/* Section Type Selector */}
          <div>
            <label className={cn(primaryText, semiBoldText, smallTextSize, "block mb-2")}>
              Data Type
            </label>
            <p className={cn(primaryText, "text-xs opacity-60 mb-2")}>
              Select whether this is food, drinks, or take-away. This determines the revenue source type the data is attributed to. Take-away revenue is generated against a Food section (since costs come from the kitchen).
            </p>
            <select
              value={sectionType}
              onChange={(e) => setSectionType(e.target.value as SectionType)}
              className="w-full px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-background dark:bg-background-dark text-primaryText dark:text-primaryText-dark focus:outline-none focus:ring-2 focus:ring-accent dark:focus:ring-accent-dark"
            >
              <option value={SectionType.FOOD}>Food</option>
              <option value={SectionType.DRINKS}>Drinks</option>
              <option value={SectionType.TAKE_AWAY}>Take Away</option>
            </select>
          </div>

          {/* Platform Selection */}
          <div>
            <label className={cn(primaryText, semiBoldText, smallTextSize, "block mb-2")}>
              Select Platforms (Multi-select)
            </label>
            <div className="relative" ref={platformDropdownRef}>
              <button
                type="button"
                onClick={() => {
                  setPlatformSearch('');
                  setIsPlatformDropdownOpen(!isPlatformDropdownOpen);
                }}
                className="w-full px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-background dark:bg-background-dark text-primaryText dark:text-primaryText-dark focus:outline-none focus:ring-2 focus:ring-accent dark:focus:ring-accent-dark flex items-center justify-between"
              >
                <span>
                  {selectedPlatforms.length === 0
                    ? 'Select platforms...'
                    : selectedPlatforms.length === 1
                    ? formatPlatformName(selectedPlatforms[0])
                    : `${selectedPlatforms.length} platforms selected`}
                </span>
                <ChevronDown className="w-4 h-4" />
              </button>
              {isPlatformDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-full bg-background dark:bg-background-dark border border-gray-300 dark:border-gray-600 rounded-md shadow-lg z-10">
                  <div className="p-3">
                    <input
                      type="search"
                      value={platformSearch}
                      onChange={(e) => setPlatformSearch(e.target.value)}
                      placeholder={t('profile.demoAutomation.searchPlatforms')}
                      aria-label={t('profile.demoAutomation.searchPlatforms')}
                      autoFocus
                      className="w-full mb-3 px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-background dark:bg-background-dark text-primaryText dark:text-primaryText-dark placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-accent dark:focus:ring-accent-dark"
                    />
                    <div className="flex items-center justify-between mb-2">
                      <span className={cn(primaryText, "text-xs font-medium opacity-70")}>Platforms</span>
                      {selectedPlatforms.length === 0 ? (
                        <button
                          onClick={handleSelectAllPlatforms}
                          className="text-xs text-accent dark:text-accent-dark hover:underline"
                        >
                          Select All
                        </button>
                      ) : (
                        <button
                          onClick={handleClearAllPlatforms}
                          className="text-xs text-accent dark:text-accent-dark hover:underline"
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {filteredPlatforms.length === 0 && (
                        <p role="status" className={cn(primaryText, "text-sm opacity-70 py-2")}>
                          {t('profile.demoAutomation.noPlatformsFound')}
                        </p>
                      )}
                      {filteredPlatforms.map(platform => (
                        <label
                          key={platform}
                          className="flex items-center gap-2 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedPlatforms.includes(platform)}
                            onChange={() => handlePlatformToggle(platform)}
                            className="rounded border-gray-300 dark:border-gray-600"
                          />
                          <span className={cn(primaryText, "text-sm")}>
                            {formatPlatformName(platform)}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {selectedPlatforms.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedPlatforms.map(platform => (
                  <span
                    key={platform}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-accent/10 dark:bg-accent-dark/10 text-accent dark:text-accent-dark rounded text-xs"
                  >
                    {formatPlatformName(platform)}
                    <button
                      onClick={() => handlePlatformToggle(platform)}
                      className="hover:opacity-70"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Catch-All Platform Selection */}
          <div>
            <label className={cn(primaryText, semiBoldText, smallTextSize, "block mb-2")}>
              Catch-All Supplier <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <p className={cn(primaryText, "text-xs opacity-60 mb-2")}>
              This supplier will be used for ingredient categories that don't have a suitable platform selected above.
            </p>
            <select
              value={catchAllPlatform}
              onChange={(e) => setCatchAllPlatform(e.target.value as Platform)}
              className="w-full px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-background dark:bg-background-dark text-primaryText dark:text-primaryText-dark focus:outline-none focus:ring-2 focus:ring-accent dark:focus:ring-accent-dark"
            >
              <option value="">Select catch-all supplier...</option>
              {allPlatforms.map(platform => (
                <option key={platform} value={platform}>
                  {formatPlatformName(platform)}
                </option>
              ))}
            </select>
          </div>

          {/* Restaurant Section Selection */}
          <div>
            <label className={cn(primaryText, semiBoldText, smallTextSize, "block mb-2")}>
              Restaurant Section <span className="text-red-500 dark:text-red-400">*</span>
            </label>
            <p className={cn(primaryText, "text-xs opacity-60 mb-2")}>
              Select which section to assign the order history data to, or create a new section.
            </p>
            <select
              value={selectedSection}
              onChange={(e) => {
                setSelectedSection(e.target.value);
                if (e.target.value !== 'create_new') {
                  setNewSectionName('');
                }
              }}
              className="w-full px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-background dark:bg-background-dark text-primaryText dark:text-primaryText-dark focus:outline-none focus:ring-2 focus:ring-accent dark:focus:ring-accent-dark"
            >
              <option value="">Select section...</option>
              {sections.map(section => (
                <option key={section.id} value={section.id}>
                  {section.name}
                </option>
              ))}
              <option value="create_new">Create new section</option>
            </select>

            {selectedSection === 'create_new' && (
              <div className="mt-3">
                <label className={cn(primaryText, semiBoldText, smallTextSize, "block mb-2")}>
                  New Section Name <span className="text-red-500 dark:text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  placeholder="e.g., Main Kitchen, Bar, Catering"
                  className="w-full px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-background dark:bg-background-dark text-primaryText dark:text-primaryText-dark focus:outline-none focus:ring-2 focus:ring-accent dark:focus:ring-accent-dark"
                />
              </div>
            )}
          </div>

          {/* Generation Options */}
          <div className="space-y-4">
            <label className={cn(primaryText, semiBoldText, smallTextSize, "block mb-2")}>
              Generation Options
            </label>

            {/* Revenue History Toggle */}
            <div className="flex items-start gap-3 p-4 rounded-md bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <input
                type="checkbox"
                id="generateRevenueHistory"
                checked={generateRevenueHistory}
                onChange={(e) => setGenerateRevenueHistory(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-accent dark:text-accent-dark focus:ring-accent dark:focus:ring-accent-dark"
              />
              <div className="flex-1">
                <label htmlFor="generateRevenueHistory" className={cn(primaryText, "text-sm font-medium cursor-pointer")}>
                  Generate Revenue History
                </label>
                <p className={cn(primaryText, "text-xs opacity-60 mt-0.5")}>
                  Create fake revenue data for the last 6 months based on the menu template
                </p>
              </div>
            </div>

            {/* Order History Toggle */}
            <div className="flex items-start gap-3 p-4 rounded-md bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <input
                type="checkbox"
                id="generateOrderHistory"
                checked={generateOrderHistory}
                onChange={(e) => setGenerateOrderHistory(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-accent dark:text-accent-dark focus:ring-accent dark:focus:ring-accent-dark"
              />
              <div className="flex-1">
                <label htmlFor="generateOrderHistory" className={cn(primaryText, "text-sm font-medium cursor-pointer")}>
                  Generate Order History
                </label>
                <p className={cn(primaryText, "text-xs opacity-60 mt-0.5")}>
                  Create fake order data from selected platforms based on the menu ingredients
                </p>
              </div>
            </div>

            {/* Inventory Toggle */}
            <div className="flex items-start gap-3 p-4 rounded-md bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <input
                type="checkbox"
                id="generateInventory"
                checked={generateInventory}
                onChange={(e) => setGenerateInventory(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-accent dark:text-accent-dark focus:ring-accent dark:focus:ring-accent-dark"
              />
              <div className="flex-1">
                <label htmlFor="generateInventory" className={cn(primaryText, "text-sm font-medium cursor-pointer")}>
                  {t('profile.demoAutomation.generateInventory')}
                </label>
                <p className={cn(primaryText, "text-xs opacity-60 mt-0.5")}>
                  {t('profile.demoAutomation.generateInventoryDescription')}
                </p>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <h3 className={cn(primaryText, semiBoldText, smallTextSize, "text-red-800 dark:text-red-300 mb-1")}>
                  {t(result ? 'profile.demoAutomation.errors.refreshTitle' : 'profile.demoAutomation.errors.title')}
                </h3>
                <p className={cn(primaryText, "text-sm text-red-700 dark:text-red-400 whitespace-pre-line break-words")}>
                  {error}
                </p>
              </div>
            </div>
          )}

          {/* Result Display */}
          {result && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className={cn(primaryText, semiBoldText, smallTextSize, "text-green-800 dark:text-green-300 mb-1")}>
                    Processing Successful
                  </h3>
                  <p className={cn(primaryText, "text-sm text-green-700 dark:text-green-400")}>
                    {result.message || 'Demo automation processed successfully'}
                  </p>
                  {result.menuData && (
                    <div className="mt-2 text-sm text-green-700 dark:text-green-400">
                      <p>Categories: {result.menuData.categories?.length || 0}</p>
                      <p>Items: {result.menuData.categories?.reduce((sum: number, cat: any) => sum + (cat.items?.length || 0), 0) || 0}</p>
                      {result.monthlyRevenue > 0 && <p>Monthly Revenue: ${parseFloat(result.monthlyRevenue).toLocaleString()}</p>}
                      {result.monthlyCosts > 0 && <p>Monthly Costs: ${parseFloat(result.monthlyCosts).toLocaleString()}</p>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 text-primaryText dark:text-primaryText-dark rounded-md font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={result ? onDone : handleSubmit}
              disabled={
                isProcessing || (!result && (
                  !selectedFile ||
                  selectedPlatforms.length === 0 ||
                  !catchAllPlatform ||
                  !selectedSection ||
                  (selectedSection === 'create_new' && !newSectionName.trim()) ||
                  (!generateRevenueHistory && !generateOrderHistory)
                ))
              }
              className="flex-1 px-6 py-3 bg-accent dark:bg-accent-dark text-white dark:text-white rounded-md font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Processing...
                </>
              ) : result ? t('profile.demoAutomation.viewDashboard') : (
                <>
                  <Upload className="h-5 w-5" />
                  Process Demo
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoAutomationModal;
